import { useCallback, useEffect, useState } from 'react';
import { Toast } from 'antd-mobile';
import i18n from '../../../i18n';
import { campaignApi } from '../../../api/campaigns';
import { ApiEnvelope, PaginatedApiEnvelope } from '../../../api/types';
import { CampaignItem, CampaignListState, CampaignUserPromotionStatus } from './types';

interface CampaignListResponse extends PaginatedApiEnvelope<CampaignItem> {}

const PROMOTION_STATUS_ORDER: Record<CampaignUserPromotionStatus, number> = {
  available: 0,
  quota_exhausted: 1,
  completed: 2,
};

function readErrorMessage(error: { response?: { data?: { error?: { message?: string } } } }, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

function sortCampaignItems(items: CampaignItem[]): CampaignItem[] {
  return [...items].sort((left, right) => {
    const leftOrder = PROMOTION_STATUS_ORDER[left.user_promotion_status || 'available'] ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = PROMOTION_STATUS_ORDER[right.user_promotion_status || 'available'] ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
  });
}

export function useCampaignListModel() {
  const [state, setState] = useState<CampaignListState>({
    loading: true,
    refreshing: false,
    items: [],
  });

  const load = useCallback(async (isRefreshing = false) => {
    setState((previous) => ({
      ...previous,
      loading: isRefreshing ? previous.loading : true,
      refreshing: isRefreshing,
    }));

    try {
      const response = await campaignApi.list({ page: 1, limit: 50 }) as { data: CampaignListResponse | ApiEnvelope<CampaignItem[]> };
      const payload = response.data.data;
      const items = sortCampaignItems(Array.isArray(payload) ? payload : response.data.data);
      setState({
        loading: false,
        refreshing: false,
        items,
      });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('campaigns:list.loadFailed')),
      });
      setState((previous) => ({ ...previous, loading: false, refreshing: false }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    state,
    reload: async () => {
      await load(true);
    },
  };
}
